export const M4_MODULE_REVIEW = {
  id: "m4-module-review",
  title: "Module 4 Review / Repaso del Módulo 4",
  topic: "Complete Module 4 review! Test everything you learned: introductions, routines, present continuous, feelings, health, weather, travel, comparatives, superlatives, geography, and making choices. Show your mastery!",
  iconType: "review",
  
  sets: [
    // SET 1: Units 01-04 (to be, present simple, present continuous)
    {
      id: "m4-final-review-s1",
      title: "Review Set 1: Introductions, Routines & Actions",
      questions: [
        {
          type: "match",
          q: "Match sentence to verb form:",
          pairs: [
            { left: "I am a teacher", right: "TO BE (fact/profession)" },
            { left: "I work every day", right: "Present simple (routine)" },
            { left: "I'm working now", right: "Present continuous (happening now)" }
          ],
          a: "completed",
          tip: "Three key structures! TO BE for facts, present simple for routines, present continuous for now!"
        },
        {
          type: "mc",
          q: "'She ___ a doctor.' (profession/job)",
          options: ["is", "works", "working"],
          a: "is",
          tip: "TO BE + profession! 'She is a doctor' = her job. Simple fact!"
        },
        {
          type: "input",
          q: "Complete: 'I ___ to work at 8am every day.' (go - routine)",
          a: "go"
        },
        {
          type: "order",
          q: "Ordena: 'Ella está cocinando la cena ahora'",
          words: ["She", "is", "cooking", "dinner", "now."],
          a: "She is cooking dinner now."
        },
        {
          type: "mc",
          q: "'They usually ___ lunch at 1pm, but today they ___ lunch at 2pm.'",
          options: ["eat / are eating (routine vs exception)", "are eating / eat", "eat / eat"],
          a: "eat / are eating (routine vs exception)",
          successtip: "✓ CONTRAST! 'Usually' = present simple (routine). 'Today' = present continuous (exception). Shows different actions!"
        },
        {
          type: "input",
          q: "Complete: 'Right now, I ___ my homework.' (am doing - happening now)",
          a: "am doing"
        },
        {
          type: "mc",
          q: "'What ___ you doing?' (asking about now)",
          options: ["are", "do", "is"],
          a: "are",
          tip: "WHAT ARE YOU DOING? Present continuous question! Asking about action happening now!"
        },
        {
          type: "order",
          q: "Ordena: '¿Trabajas los fines de semana?'",
          words: ["Do", "you", "work", "on", "weekends?"],
          a: "Do you work on weekends?"
        },
        {
          type: "input",
          q: "Complete: 'She ___ like coffee, but she ___ tea.' (doesn't / likes)",
          a: "doesn't / likes"
        },
        {
          type: "mc",
          q: "'I ___ TV every night' vs 'I ___ TV right now' - difference:",
          options: [
            "First = routine (watch). Second = happening now (am watching)",
            "Both are routines",
            "Both are happening now"
          ],
          a: "First = routine (watch). Second = happening now (am watching)",
          successtip: "✓ KEY DIFFERENCE! Present simple = habits/routines. Present continuous = actions happening at this moment! Master this!"
        },
        {
          type: "input",
          q: "Complete: '___ she working today?' (Is - present continuous question)",
          a: "Is"
        },
        {
          type: "mc",
          q: "'He ___ tennis every Saturday' (routine - present simple)",
          options: ["plays", "is playing", "play"],
          a: "plays",
          tip: "PLAYS! Present simple! 'Every Saturday' = routine = use present simple + S for he/she/it!"
        }
      ]
    },

    // SET 2: Units 05-09 (state verbs, feelings, transportation, exceptions)
    {
      id: "m4-final-review-s2",
      title: "Review Set 2: Feelings, Health & Exceptions",
      questions: [
        {
          type: "match",
          q: "Match feeling to meaning:",
          pairs: [
            { left: "excited", right: "happy and enthusiastic (positive)" },
            { left: "nervous", right: "worried (negative)" },
            { left: "tired", right: "need sleep/rest" }
          ],
          a: "completed",
          tip: "Emotion vocabulary! Essential for expressing how you feel!"
        },
        {
          type: "mc",
          q: "'I ___ very happy today!' (feeling)",
          options: ["am", "have", "do"],
          a: "am",
          tip: "I AM + feeling! TO BE + adjective to express emotions! 'I am happy/sad/tired/excited'!"
        },
        {
          type: "input",
          q: "Complete: 'I have a bad ___.' (headache - head hurts)",
          a: "headache"
        },
        {
          type: "order",
          q: "Ordena: 'Mi espalda me duele mucho'",
          words: ["My", "back", "hurts", "a", "lot."],
          a: "My back hurts a lot."
        },
        {
          type: "mc",
          q: "'I ___ a broken arm.' (health problem - possession)",
          options: ["have", "am", "do"],
          a: "have",
          successtip: "✓ HAVE + health problem! 'I have a headache / a cold / a broken arm'. Use HAVE for health issues!"
        },
        {
          type: "input",
          q: "Complete: 'I usually drive to work, ___ today I'm taking the bus.' (but - showing exception)",
          a: "but"
        },
        {
          type: "mc",
          q: "'I love books.' - 'love' is a ___ verb (cannot use in continuous)",
          options: ["state", "action", "doing"],
          a: "state",
          tip: "STATE VERB! Cannot say 'I am loving' ❌. State verbs describe states/feelings, not actions!"
        },
        {
          type: "order",
          q: "Ordena: 'Normalmente camino, pero hoy estoy manejando'",
          words: ["I", "usually", "walk,", "but", "today", "I'm", "driving."],
          a: "I usually walk, but today I'm driving."
        },
        {
          type: "input",
          q: "Complete: 'She goes to work by ___.' (subway/bus/car - transportation)",
          a: "subway / bus / car"
        },
        {
          type: "mc",
          q: "'I want a new laptop.' - Can you say 'I am wanting'?",
          options: [
            "No! 'Want' is a state verb - use 'I want' only",
            "Yes, always use 'I am wanting'",
            "Both are correct"
          ],
          a: "No! 'Want' is a state verb - use 'I want' only",
          successtip: "✓ STATE VERBS! Want, like, love, hate, know, need = state verbs. NO continuous form! Never 'wanting/liking/loving' ❌!"
        },
        {
          type: "input",
          q: "Complete: 'How ___ you feeling?' (are - asking about emotions)",
          a: "are"
        },
        {
          type: "mc",
          q: "'I usually work from home, but this week ___' - exception:",
          options: [
            "I'm working at the office (temporary change)",
            "I work from home",
            "I worked from home"
          ],
          a: "I'm working at the office (temporary change)",
          tip: "EXCEPTION! 'Usually' = routine. 'This week' = temporary different. Present continuous for exceptions!"
        }
      ]
    },

    // SET 3: Units 10-14 (body, health, weather, travel)
    {
      id: "m4-final-review-s3",
      title: "Review Set 3: Body, Weather & Travel",
      questions: [
        {
          type: "match",
          q: "Match body part to description:",
          pairs: [
            { left: "head", right: "top of body where brain is" },
            { left: "stomach", right: "where you digest food" },
            { left: "knee", right: "joint in the middle of leg" }
          ],
          a: "completed",
          tip: "Body vocabulary! Important for health and describing pain!"
        },
        {
          type: "mc",
          q: "'What's the weather ___?' (asking about weather)",
          options: ["like", "is", "do"],
          a: "like",
          tip: "'WHAT'S THE WEATHER LIKE?' Fixed expression! 'Like' here is preposition, not verb!"
        },
        {
          type: "input",
          q: "Complete: 'It's very hot today. It's 35 degrees. I'm ___!' (boiling - very hot)",
          a: "boiling"
        },
        {
          type: "order",
          q: "Ordena: 'Está lloviendo mucho hoy'",
          words: ["It's", "raining", "a", "lot", "today."],
          a: "It's raining a lot today."
        },
        {
          type: "mc",
          q: "'It's very cold. It's -20°C. I'm ___!' (extremely cold)",
          options: ["freezing", "boiling", "warm"],
          a: "freezing",
          successtip: "✓ FREEZING! Informal way to say 'very cold'! Boiling = very hot. Freezing = very cold. Common expressions!"
        },
        {
          type: "input",
          q: "Complete: 'There's a lot of ___. I can't see anything!' (fog - foggy weather)",
          a: "fog"
        },
        {
          type: "mc",
          q: "'I'm going on ___.' (vacation/holiday - trip)",
          options: ["vacation", "vacations", "a vacation"],
          a: "vacation",
          tip: "'ON VACATION' (US) or 'ON HOLIDAY' (UK)! Fixed expression! No article!"
        },
        {
          type: "order",
          q: "Ordena: 'Voy a abordar el avión pronto'",
          words: ["I'm", "going", "to", "board", "the", "plane", "soon."],
          a: "I'm going to board the plane soon."
        },
        {
          type: "input",
          q: "Complete: 'I need to ___ my bags before the trip.' (pack)",
          a: "pack"
        },
        {
          type: "mc",
          q: "'I ___ at the airport at 3pm yesterday.' - Wait! Do you know this tense?",
          options: [
            "This uses past tense - you don't know yet! Stay in present!",
            "arrived",
            "arrive"
          ],
          a: "This uses past tense - you don't know yet! Stay in present!",
          successtip: "✓ CAREFUL! Questions should use present simple/continuous only! Past tense comes in Module 5!"
        },
        {
          type: "input",
          q: "Complete: 'My leg ___. I have a pain in my leg.' (hurts)",
          a: "hurts"
        },
        {
          type: "mc",
          q: "'It's ___ outside. There are many clouds.' (cloudy weather)",
          options: ["cloudy", "cloud", "clouds"],
          a: "cloudy",
          tip: "IT'S + ADJECTIVE! Cloudy, sunny, rainy, snowy, windy, foggy = weather adjectives!"
        }
      ]
    },

    // SET 4: Units 15-17 (comparatives, superlatives, geography)
    {
      id: "m4-final-review-s4",
      title: "Review Set 4: Comparisons & Geography",
      questions: [
        {
          type: "match",
          q: "Match comparison type:",
          pairs: [
            { left: "bigger than", right: "Comparative (comparing 2)" },
            { left: "the biggest", right: "Superlative (extreme of 3+)" },
            { left: "good → better → the best", right: "Irregular forms" }
          ],
          a: "completed",
          tip: "Comparison structures! Comparative = 2 things. Superlative = 3+ things (find the extreme)!"
        },
        {
          type: "mc",
          q: "'Toronto is ___ than Montreal.' (bigger - comparing 2 cities)",
          options: ["bigger", "biggest", "the biggest"],
          a: "bigger",
          tip: "BIGGER THAN! Comparative! Two cities = use comparative + THAN!"
        },
        {
          type: "input",
          q: "Complete: 'Mount Everest is ___ highest mountain in the world.' (the)",
          a: "the"
        },
        {
          type: "order",
          q: "Ordena: 'Este restaurante es más caro que ese'",
          words: ["This", "restaurant", "is", "more", "expensive", "than", "that", "one."],
          a: "This restaurant is more expensive than that one."
        },
        {
          type: "mc",
          q: "'This hotel is good, but that one is ___.' (comparing quality - irregular)",
          options: ["better", "gooder", "more good"],
          a: "better",
          successtip: "✓ BETTER! Irregular! NEVER say 'gooder' or 'more good' ❌! Good → better → the best. Memorize!"
        },
        {
          type: "input",
          q: "Complete: 'This is ___ worst movie I know!' (the - superlative)",
          a: "the"
        },
        {
          type: "mc",
          q: "'A large body of salt water' = ?",
          options: ["ocean", "lake", "river"],
          a: "ocean",
          tip: "OCEAN! Salt water, very large. Lake = fresh water. River = flowing water!"
        },
        {
          type: "order",
          q: "Ordena: 'Las montañas son más altas que las colinas'",
          words: ["Mountains", "are", "higher", "than", "hills."],
          a: "Mountains are higher than hills."
        },
        {
          type: "input",
          q: "Complete: 'A ___ is a mountain that erupts with lava.' (volcano)",
          a: "volcano"
        },
        {
          type: "mc",
          q: "'The Sahara is ___ desert in the world.' (extreme - superlative)",
          options: ["the biggest", "bigger", "big"],
          a: "the biggest",
          successtip: "✓ THE BIGGEST! Superlative! Always use THE before superlative! Comparing to ALL deserts!"
        },
        {
          type: "input",
          q: "Complete: 'Canada has many beautiful ___.' (lakes - geographical feature)",
          a: "lakes"
        },
        {
          type: "mc",
          q: "'Farther' and 'further' are irregular forms of:",
          options: ["far (distance)", "fast (speed)", "fat (size)"],
          a: "far (distance)",
          tip: "FAR → FARTHER/FURTHER! Irregular! Distance comparisons. Both forms correct!"
        }
      ]
    },

    // SET 5: Unit 18 (which/what, choices, irregulars) + Integration
    {
      id: "m4-final-review-s5",
      title: "Review Set 5: Making Choices & Final Integration",
      questions: [
        {
          type: "match",
          q: "Match question word to use:",
          pairs: [
            { left: "WHICH (limited options)", right: "Which city - A or B?" },
            { left: "WHAT (open question)", right: "What is your favorite?" },
            { left: "OR (shows choice)", right: "Coffee or tea?" }
          ],
          a: "completed",
          tip: "Choice questions! WHICH = specific options. WHAT = open. OR = shows the options!"
        },
        {
          type: "mc",
          q: "'___ city do you prefer - Toronto or Vancouver?' (2 specific options)",
          options: ["Which", "What", "Where"],
          a: "Which",
          tip: "WHICH! Two specific cities = limited choice. Use WHICH when options are clear!"
        },
        {
          type: "input",
          q: "Complete: 'I like coffee ___ tea.' (and - expressing you like both)",
          a: "and"
        },
        {
          type: "order",
          q: "Ordena: '¿Cuál prefieres - verano o invierno?'",
          words: ["Which", "do", "you", "prefer", "-", "summer", "or", "winter?"],
          a: "Which do you prefer - summer or winter?"
        },
        {
          type: "mc",
          q: "'You can choose ___ this ___ that.' (one or the other)",
          options: ["either / or", "neither / nor", "both / and"],
          a: "either / or",
          successtip: "✓ EITHER...OR! Choice between two! 'Either A or B' = pick one! Advanced structure!"
        },
        {
          type: "input",
          q: "Complete: '___ cities are beautiful!' (Both - talking about two together)",
          a: "Both"
        },
        {
          type: "mc",
          q: "Complete integration: 'I usually work from home, but today I ___ at the office because my computer ___ broken.'",
          options: [
            "am working / is (exception + reason with TO BE)",
            "work / is",
            "am working / works"
          ],
          a: "am working / is (exception + reason with TO BE)",
          tip: "INTEGRATION! Present continuous (exception today) + TO BE (fact about computer)!"
        },
        {
          type: "order",
          q: "Ordena: 'Este es el mejor restaurante de la ciudad'",
          words: ["This", "is", "the", "best", "restaurant", "in", "the", "city."],
          a: "This is the best restaurant in the city."
        },
        {
          type: "input",
          q: "Complete: 'What ___ you doing right now?' (are - present continuous question)",
          a: "are"
        },
        {
          type: "mc",
          q: "Full sentence: 'My name ___ Sarah. I ___ from Mexico. I ___ in Toronto. I ___ as a designer. Right now, I ___ English.'",
          options: [
            "is / am / live / work / am learning (TO BE + present simple + present continuous)",
            "is / am / am living / am working / learn",
            "am / is / live / work / learn"
          ],
          a: "is / am / live / work / am learning (TO BE + present simple + present continuous)",
          successtip: "✓ COMPLETE INTEGRATION! TO BE for facts (name, origin). Present simple for routines (live, work). Present continuous for now (learning)! All Module 4 structures!"
        },
        {
          type: "input",
          q: "Complete: 'I think summer is ___ than winter because the weather is ___.' (better / better)",
          a: "better / better"
        },
        {
          type: "mc",
          q: "FINAL MASTERY - Real conversation:\n\nA: 'Hi! What's your name?'\nB: 'I ___ Carlos. I ___ 30 years old.'\nA: 'Where ___ you from?'\nB: 'I ___ from Mexico, but I ___ in Toronto now.'\nA: 'What ___ you do?'\nB: 'I ___ a graphic designer. I ___ at a small company.'\nA: 'What ___ you doing right now?'\nB: 'I ___ coffee with a friend. We usually ___ on Saturdays!'\n\nCorrect words:",
          options: [
            "am / am / are / am / live / do / am / work / are / am having / meet",
            "is / is / is / is / am living / am / am / am working / is / have / are meeting",
            "am / have / do / from / living / do / designer / working / do / having / meeting"
          ],
          a: "am / am / are / are / am / live / do / am / work / are / am having / meet",
          successtip: "✓ MODULE 4 COMPLETE! 🎉 You mastered: TO BE (introductions, facts, feelings), present simple (routines, habits, facts), present continuous (actions now, exceptions, future plans), state verbs (can't use continuous), health/body/weather vocabulary, transportation/travel vocabulary, comparatives (bigger than, better, worse, farther), superlatives (the biggest, the best, the worst), geographical features (mountains, oceans, deserts, etc.), WHICH vs WHAT (limited vs open questions), AND/OR (connecting/choosing), either/or, neither/nor, both/and! From zero to complete beginner conversations! You can introduce yourself, describe routines, talk about what's happening now, express feelings, discuss health and weather, make comparisons, describe places, and make decisions! Ready for Module 5! 🚀🏆"
        }
      ]
    }
  ]
};
